// ============================================================================
// MarketApp — корневой экран клиентской витрины маркетплейса.
//
// Внутренняя навигация — состояние (как мобильное приложение): главная →
// мастер заявки → «Мои заявки» → карточка → чат.
//
// ГЕЙТ ВХОДА (роли не путаем):
//   • гость — НЕ попадает в мастер заявки: экран «нужен вход» ведёт на
//     вход/регистрацию, намерение «вернуться в мастер» сохраняется;
//   • клиент — витрина и мастер заявки; черновик восстанавливается после входа;
//   • мастер — СВОЙ КАБИНЕТ (лента свободных и взятых заявок), а не витрина
//     клиента. Мастер может явно начать заявку (CTA «Оставить заявку»).
//
// Состояния (загрузка/ошибка) — явные: проверка роли, вход, черновик.
// ============================================================================

import { useEffect, useRef, useState } from 'react';
import type { MarketRequest } from '@/lib/market';
import { useAuthStore } from '@/store/authStore';
import { logErrorToJournal } from '@/lib/errors';
import { goAuthGate } from '@/lib/navigate';
import { MARKET_CLIENT_APP_CODE } from '@/lib/marketConstants';
import { AuthRequiredScreen } from '@/components/auth/AuthRequiredScreen';
import { MarketHomePage } from '@/pages/MarketHomePage';
import { MarketRequestsPage } from '@/pages/MarketRequestsPage';
import { MarketWizardPage } from '@/pages/MarketWizardPage';
import { MarketRequestDetailPage } from '@/pages/MarketRequestDetailPage';
import { MarketChatPage } from '@/pages/MarketChatPage';
import { MarketDisputePage } from '@/pages/MarketDisputePage';
import { MarketMasterFeedPage } from '@/pages/MarketMasterFeedPage';
import { MarketError, MarketLoading } from '@/components/market/MarketState';
import { MarketTopBar } from '@/components/market/MarketTopBar';
import { useI18n } from '@/lib/i18n/I18nProvider';
import { useMarketRole } from '@/components/market/useMarketRole';
import { MarketMasterWalletPage } from '@/pages/MarketMasterWalletPage';
import { MarketMasterAreaPage } from '@/pages/MarketMasterAreaPage';
import { MarketOperatorPage } from '@/pages/MarketOperatorPage';
import { MarketClientProfile } from '@/components/profile/market/MarketClientProfile';
import { useMarketOperator } from '@/components/market/useMarketOperator';
import {
  clearMarketDraft,
  clearMarketResume,
  consumeMarketResume,
  loadMarketDraft,
  setMarketResume,
  type MarketDraft,
  type MarketResume,
} from '@/components/market/marketDraft';

type RequestOrigin = 'home' | 'requests';

type Screen =
  | { name: 'home' }
  | { name: 'wizard'; categoryId?: string; resumeDraft?: boolean }
  | { name: 'requests' }
  | { name: 'request'; requestId: string; from: RequestOrigin }
  | { name: 'chat'; request: MarketRequest; from: RequestOrigin }
  | { name: 'feed' }
  | { name: 'area'; from: 'feed' | 'profile' }
  | { name: 'feed-chat'; request: MarketRequest }
  | { name: 'wallet'; from: 'home' | 'feed' }
  | { name: 'profile'; from: 'home' | 'feed' | 'requests' }
  | { name: 'dispute'; request: MarketRequest; backTo: Screen }
  | { name: 'operator' };

export function MarketApp() {
  const { t } = useI18n();
  const isAuth = useAuthStore((s) => s.isAuthenticated);
  const userId = String(useAuthStore((s) => s.user?.id || ''));
  const roleState = useMarketRole(userId, isAuth);
  const operatorAccess = useMarketOperator();

  const [screen, setScreen] = useState<Screen>({ name: 'home' });
  const [draft, setDraft] = useState<MarketDraft | null>(null);
  const initialized = useRef(false);

  // Первичная навигация после проверки роли: намерение входа → мастер заявки;
  // мастер без явного намерения → свой кабинет (лента заявок); клиент → витрина.
  useEffect(() => {
    if (!isAuth || roleState.status !== 'ready' || initialized.current) return;
    initialized.current = true;
    const savedDraft = loadMarketDraft(userId);
    setDraft(savedDraft);
    const resume = consumeMarketResume();
    // Явное намерение (вход из витрины) или незаконченный черновик клиента —
    // сразу в мастер заявки: после входа пользователь продолжает с того же шага.
    if (resume === 'wizard') {
      setScreen({ name: 'wizard', resumeDraft: true });
      return;
    }
    // Оператор маркетплейса (и администратор) открывают рабочую очередь,
    // а не витрину клиента; вернуться на витрину можно кнопкой «Назад».
    if (operatorAccess.isOperator) {
      setScreen({ name: 'operator' });
      return;
    }
    if (roleState.role === 'master') {
      // У мастера корень — СВОЙ КАБИНЕТ; черновик не уводит его из кабинета.
      setScreen({ name: 'feed' });
      return;
    }
    if (savedDraft) {
      setScreen({ name: 'wizard', resumeDraft: true });
      return;
    }
    setScreen({ name: 'home' });
  }, [isAuth, roleState.status, roleState.role, userId, operatorAccess.isOperator]);

  const master = roleState.role === 'master';
  const homeScreen: Screen = master ? { name: 'feed' } : { name: 'home' };

  /** Намерение входа: после входа вернуться в мастер заявки (или на главную). */
  const requireAuth = (resume: MarketResume) => {
    try {
      setMarketResume(resume);
      goAuthGate({ app: MARKET_CLIENT_APP_CODE });
    } catch (e) {
      logErrorToJournal(e, 'Витрина маркетплейса: переход ко входу');
    }
  };

  // Гость не должен открывать мастер заявки — только вход/регистрация.
  if (!isAuth) {
    return (
      <div className="flex h-full flex-col bg-background text-foreground">
        <MarketTopBar title={t('market.title')} subtitle={t('market.showcase')} />
        <div className="flex-1 overflow-y-auto">
          <AuthRequiredScreen
            title={t('market.leave_request')}
            description="Заявку можно создать после входа. Войдите или зарегистрируйтесь — и вернётесь к заполнению заявки."
            note="Если вы уже начали заполнять заявку, черновик сохранится на этом устройстве."
            onAction={() => requireAuth('wizard')}
          />
        </div>
      </div>
    );
  }

  // Явные состояния проверки роли: загрузка и ошибка с повтором.
  if (roleState.status === 'loading' || (roleState.status === 'ready' && !initialized.current)) {
    return (
      <div className="flex h-full flex-col bg-background text-foreground">
        <MarketTopBar title={t('market.title')} subtitle={t('market.checking_access')} />
        <div className="flex-1 overflow-y-auto">
          <MarketLoading text="Проверяем доступ к заявкам…" />
        </div>
      </div>
    );
  }

  if (roleState.status === 'error') {
    return (
      <div className="flex h-full flex-col bg-background text-foreground">
        <MarketTopBar title={t('market.title')} subtitle={t('market.mode_error')} />
        <div className="flex-1 overflow-y-auto p-4">
          <MarketError
            failure={roleState.failure || { kind: 'other', message: 'Не удалось определить режим работы' }}
            onRetry={roleState.retry}
          />
        </div>
      </div>
    );
  }

  const openRequest = (requestId: string, from: RequestOrigin) => {
    setScreen({ name: 'request', requestId, from });
  };

  const backHome = () => {
    setDraft(loadMarketDraft(userId));
    setScreen(homeScreen);
  };

  if (screen.name === 'wizard') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketWizardPage
          initialCategoryId={screen.categoryId}
          resumeDraft={screen.resumeDraft}
          onRequireAuth={() => requireAuth('wizard')}
          onCancel={backHome}
          onOpenRequest={(requestId) => openRequest(requestId, 'requests')}
          onOpenRequests={() => setScreen({ name: 'requests' })}
          onHome={backHome}
        />
      </div>
    );
  }

  if (screen.name === 'requests') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketRequestsPage
          onBack={backHome}
          onCreate={() => setScreen({ name: 'wizard', resumeDraft: Boolean(draft) })}
          onOpenRequest={(requestId) => openRequest(requestId, 'requests')}
          onOpenProfile={() => setScreen({ name: 'profile', from: 'requests' })}
        />
      </div>
    );
  }

  if (screen.name === 'request') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketRequestDetailPage
          requestId={screen.requestId}
          onBack={() => setScreen(screen.from === 'home' ? homeScreen : { name: 'requests' })}
          onOpenChat={(request) => setScreen({ name: 'chat', request, from: screen.from })}
          onOpenDispute={(request) => setScreen({ name: 'dispute', request, backTo: screen })}
        />
      </div>
    );
  }

  if (screen.name === 'chat') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketChatPage
          request={screen.request}
          onBack={() => setScreen({ name: 'request', requestId: screen.request.id, from: screen.from })}
          onOpenDispute={(request) => setScreen({ name: 'dispute', request, backTo: screen })}
        />
      </div>
    );
  }

  if (screen.name === 'dispute') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketDisputePage request={screen.request} onBack={() => setScreen(screen.backTo)} />
      </div>
    );
  }

  if (screen.name === 'feed') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketMasterFeedPage
          onBack={master ? undefined : backHome}
          onOpenChat={(request) => setScreen({ name: 'feed-chat', request })}
          onOpenWallet={() => setScreen({ name: 'wallet', from: 'feed' })}
          onOpenArea={() => setScreen({ name: 'area', from: 'feed' })}
          onOpenProfile={() => setScreen({ name: 'profile', from: 'feed' })}
        />
      </div>
    );
  }

  if (screen.name === 'area') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketMasterAreaPage
          onBack={() => setScreen(
            screen.from === 'profile'
              ? { name: 'profile', from: 'feed' }
              : { name: 'feed' },
          )}
        />
      </div>
    );
  }

  if (screen.name === 'wallet') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketMasterWalletPage
          onBack={() => setScreen(screen.from === 'feed' ? { name: 'feed' } : { name: 'home' })}
        />
      </div>
    );
  }

  if (screen.name === 'feed-chat') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketChatPage
          request={screen.request}
          onBack={() => setScreen({ name: 'feed' })}
          onOpenDispute={(request) => setScreen({ name: 'dispute', request, backTo: screen })}
        />
      </div>
    );
  }

  if (screen.name === 'profile') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketClientProfile
          role={master ? 'master' : 'client'}
          onBack={() => setScreen(
            screen.from === 'feed'
              ? { name: 'feed' }
              : screen.from === 'requests'
                ? { name: 'requests' }
                : homeScreen,
          )}
          onOpenMasterFeed={() => setScreen({ name: 'feed' })}
          onOpenWallet={() => setScreen({ name: 'wallet', from: 'feed' })}
          onOpenArea={() => setScreen({ name: 'area', from: 'profile' })}
        />
      </div>
    );
  }

  if (screen.name === 'operator') {
    return (
      <div className="h-full w-full bg-background text-foreground">
        <MarketOperatorPage onBack={backHome} />
      </div>
    );
  }

  return (
    <div className="h-full w-full bg-background text-foreground">
      <MarketHomePage
        authenticated={isAuth}
        onRequireAuth={() => requireAuth('wizard')}
        draft={draft}
        onResumeDraft={() => setScreen({ name: 'wizard', resumeDraft: true })}
        onDiscardDraft={() => {
          clearMarketDraft();
          clearMarketResume();
          setDraft(null);
        }}
        onStart={(categoryId) => setScreen({ name: 'wizard', categoryId })}
        onOpenRequests={() => setScreen({ name: 'requests' })}
        onOpenRequest={(requestId) => openRequest(requestId, 'home')}
        onOpenMasterFeed={() => setScreen({ name: 'feed' })}
        onOpenWallet={() => setScreen({ name: 'wallet', from: 'home' })}
        onOpenProfile={() => setScreen({ name: 'profile', from: 'home' })}
        onOpenOperator={operatorAccess.isOperator ? () => setScreen({ name: 'operator' }) : undefined}
      />
    </div>
  );
}
